import { useState } from "react";
import { sendInvoiceEmail } from "../services/invoiceEmailService";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";

const OVERLAY = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.65)",
  backdropFilter: "blur(3px)",
  zIndex: 1000,
};
const MODAL_BASE = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%,-50%)",
  background: "#1a2535",
  border: "1px solid #2d3b4e",
  borderRadius: 12,
  padding: "24px",
  zIndex: 1001,
  boxShadow: "0 24px 64px rgba(0,0,0,0.55)",
};

/**
 * Modal para enviar correo de facturas.
 *
 * Props:
 *   facturas    – array de objetos factura (deben tener id_factura, numero_factura, monto, filial)
 *   onClose()   – cerrar
 *   onSuccess() – callback cuando se envió al menos uno correctamente
 */
export default function SendEmailModal({ facturas = [], onClose, onSuccess }) {
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null); // { enviados, errores }

  const multiple = facturas.length > 1;

  async function handleSend() {
    setSending(true);
    try {
      const ids = facturas.map((f) => f.id_factura ?? f.id);
      const data = await sendInvoiceEmail(ids);
      setResult(data);
      if (data.total_enviados > 0 && onSuccess) onSuccess();
    } catch (e) {
      setResult({
        enviados: [],
        errores: [{ factura: "—", error: e.message }],
        total_enviados: 0,
        total_errores: 1,
      });
    } finally {
      setSending(false);
    }
  }

  // ── Vista de resultado ───────────────────────────────────────────────
  if (result) {
    return (
      <>
        <div onClick={onClose} style={OVERLAY} />
        <div style={{ ...MODAL_BASE, width: "min(500px,93vw)" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 20,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  background: "rgba(249,115,22,0.15)",
                  border: "1px solid rgba(249,115,22,0.3)",
                  borderRadius: 9,
                  padding: "8px 10px",
                  color: "#f97316",
                }}
              >
                <i
                  className="fa-solid fa-envelope"
                  style={{ fontSize: "1.1rem" }}
                />
              </div>
              <h3
                style={{
                  margin: 0,
                  color: "#f1f5f9",
                  fontSize: "1rem",
                  fontWeight: 700,
                }}
              >
                Resultado del envío
              </h3>
            </div>
            <CloseBtn onClick={onClose} />
          </div>

          <div style={{ borderTop: "1px solid #2d3b4e", marginBottom: 20 }} />

          {/* Resumen */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginBottom: 20,
            }}
          >
            <div
              style={{
                background: "rgba(74,222,128,0.08)",
                border: "1px solid rgba(74,222,128,0.25)",
                borderRadius: 8,
                padding: "12px 16px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "1.8rem",
                  fontWeight: 800,
                  color: "#4ade80",
                }}
              >
                {result.total_enviados}
              </div>
              <div style={{ fontSize: "0.78rem", color: "#6b7280" }}>
                Enviados correctamente
              </div>
            </div>
            <div
              style={{
                background:
                  result.total_errores > 0
                    ? "rgba(239,68,68,0.08)"
                    : "rgba(55,65,81,0.2)",
                border: `1px solid ${result.total_errores > 0 ? "rgba(239,68,68,0.25)" : "#374151"}`,
                borderRadius: 8,
                padding: "12px 16px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "1.8rem",
                  fontWeight: 800,
                  color: result.total_errores > 0 ? "#f87171" : "#6b7280",
                }}
              >
                {result.total_errores}
              </div>
              <div style={{ fontSize: "0.78rem", color: "#6b7280" }}>
                Con error
              </div>
            </div>
          </div>

          {/* Detalles de enviados */}
          {result.enviados.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "#6b7280",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  marginBottom: 6,
                }}
              >
                Enviados
              </div>
              {result.enviados.map((e, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "6px 10px",
                    borderRadius: 6,
                    background: "rgba(74,222,128,0.06)",
                    marginBottom: 4,
                    fontSize: "0.83rem",
                  }}
                >
                  <span style={{ color: "#f1f5f9", fontWeight: 600 }}>
                    {e.factura}
                  </span>
                  <span style={{ color: "#4ade80" }}>{e.destinatario}</span>
                </div>
              ))}
            </div>
          )}

          {/* Detalles de errores */}
          {result.errores.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "#f87171",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  marginBottom: 6,
                }}
              >
                Errores
              </div>
              {result.errores.map((e, i) => (
                <div
                  key={i}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 6,
                    background: "rgba(239,68,68,0.07)",
                    border: "1px solid rgba(239,68,68,0.2)",
                    marginBottom: 4,
                    fontSize: "0.82rem",
                  }}
                >
                  <span style={{ color: "#fca5a5", fontWeight: 600 }}>
                    {e.factura}
                  </span>
                  <span style={{ color: "#94a3b8", marginLeft: 8 }}>
                    — {e.error}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div
            style={{
              borderTop: "1px solid #2d3b4e",
              marginTop: 20,
              paddingTop: 16,
              textAlign: "right",
            }}
          >
            <button
              onClick={onClose}
              style={{
                padding: "8px 20px",
                background: "#f97316",
                color: "#fff",
                border: "none",
                borderRadius: 7,
                fontSize: "0.84rem",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Cerrar
            </button>
          </div>
        </div>
      </>
    );
  }

  // ── Vista de confirmación ────────────────────────────────────────────
  return (
    <>
      <div onClick={onClose} style={OVERLAY} />
      <div style={{ ...MODAL_BASE, width: "min(480px,93vw)" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                background: "rgba(249,115,22,0.15)",
                border: "1px solid rgba(249,115,22,0.3)",
                borderRadius: 9,
                padding: "8px 10px",
                color: "#f97316",
              }}
            >
              <i
                className="fa-solid fa-envelope"
                style={{ fontSize: "1.1rem" }}
              />
            </div>
            <div>
              <h3
                style={{
                  margin: 0,
                  color: "#f1f5f9",
                  fontSize: "1rem",
                  fontWeight: 700,
                }}
              >
                {multiple
                  ? `Enviar ${facturas.length} correos`
                  : "Enviar correo de factura"}
              </h3>
              <p
                style={{
                  margin: "2px 0 0",
                  color: "#64748b",
                  fontSize: "0.77rem",
                }}
              >
                Se enviará al email principal del propietario de cada filial
              </p>
            </div>
          </div>
          <CloseBtn onClick={onClose} />
        </div>

        <div style={{ borderTop: "1px solid #2d3b4e", marginBottom: 20 }} />

        {/* Lista de facturas a enviar */}
        <div style={{ maxHeight: 220, overflowY: "auto", marginBottom: 16 }}>
          {facturas.map((f, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 12px",
                borderRadius: 6,
                background: i % 2 === 0 ? "rgba(55,65,81,0.2)" : "transparent",
                marginBottom: 2,
                fontSize: "0.84rem",
              }}
            >
              <span style={{ fontWeight: 600, color: "#f1f5f9" }}>
                {f.numero_factura}
              </span>
              <span style={{ color: "#9ca3af" }}>
                Filial {f.filial?.idFilial ?? "—"}
              </span>
              <span style={{ color: "#f97316", fontWeight: 600 }}>
                ₡{formatCurrency(f.monto)}
              </span>
            </div>
          ))}
        </div>

        {/* Info */}
        <div
          style={{
            background: "rgba(96,165,250,0.07)",
            border: "1px solid rgba(96,165,250,0.2)",
            borderLeft: "3px solid #60a5fa",
            borderRadius: 6,
            padding: "10px 14px",
            color: "#bfdbfe",
            fontSize: "0.82rem",
            marginBottom: 20,
          }}
        >
          <i className="fa-solid fa-circle-info" style={{ marginRight: 7 }} />
          Si una filial no tiene email principal registrado, esa factura
          aparecerá como error en el resultado.
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            onClick={onClose}
            disabled={sending}
            style={{
              padding: "8px 16px",
              background: "transparent",
              border: "1px solid #2d3b4e",
              borderRadius: 7,
              color: "#64748b",
              fontSize: "0.84rem",
              cursor: "pointer",
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSend}
            disabled={sending}
            style={{
              padding: "8px 20px",
              background: sending ? "rgba(249,115,22,0.5)" : "#f97316",
              color: "#fff",
              border: "none",
              borderRadius: 7,
              fontSize: "0.84rem",
              fontWeight: 600,
              cursor: sending ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: 7,
            }}
          >
            {sending ? (
              <>
                <i className="fa-solid fa-circle-notch fa-spin" /> Enviando…
              </>
            ) : (
              <>
                <i className="fa-solid fa-paper-plane" />{" "}
                {multiple
                  ? `Enviar ${facturas.length} correos`
                  : "Enviar correo"}
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
}

function CloseBtn({ onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: "none",
        border: "none",
        color: "#4b5e75",
        cursor: "pointer",
        fontSize: "1.1rem",
      }}
      onMouseOver={(e) => (e.currentTarget.style.color = "#9ca3af")}
      onMouseOut={(e) => (e.currentTarget.style.color = "#4b5e75")}
    >
      ✕
    </button>
  );
}
